import { Alert, LoadingButton, Paper, Typography } from "@firecms/ui";
import { type AdminFeedbackResultsResult } from "@repo/common";
import { useQuery } from "@tanstack/react-query";

import AdminToolPage from "components/molecules/AdminToolPage";
import { getErrorMessage } from "lib/error.utils";
import { queryKeys } from "lib/query-keys";
import { fetchAdminFeedbackResults } from "services/admin-tools/adminTools";

const TOOL_DESCRIPTION =
  "Wertet die Firestore-Collection `feedback` aus: Durchschnittswerte der Bewertungsfragen, Anzahl Weiterempfehlungen und alle Freitext-Antworten.";

const formatFeedbackTimestamp = (createdAt: string): string => {
  if (!createdAt) {
    return "Zeitpunkt unbekannt";
  }

  return new Date(createdAt).toLocaleString("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const AdminFeedbackResults: React.FC = () => {
  const { data, error, isFetching, refetch, isFetched } = useQuery<AdminFeedbackResultsResult>({
    queryKey: queryKeys.adminTools.feedbackResults(),
    queryFn: fetchAdminFeedbackResults,
    enabled: false,
  });

  return (
    <AdminToolPage title="Feedback-Auswertung" description={TOOL_DESCRIPTION}>
      <LoadingButton loading={isFetching} disabled={isFetching} onClick={() => void refetch()}>
        Auswerten
      </LoadingButton>

      {error ? <Alert color="error">{getErrorMessage(error)}</Alert> : null}

      {isFetched && data ? (
        <div className="flex flex-col gap-4">
          <Paper className="flex flex-col gap-2 p-4">
            <Typography>
              Gesamtanzahl Feedbacks: <strong>{data.totalFeedbackCount.toLocaleString("de-DE")}</strong>
            </Typography>
            {data.metrics.map((metric) => (
              <Typography key={metric.label}>
                {metric.label}: <strong>{metric.value}</strong>
              </Typography>
            ))}
          </Paper>

          {data.writtenFeedbacks.length > 0 ? (
            <div className="flex flex-col gap-3">
              <Typography variant="h6">Freitext-Feedbacks ({data.writtenFeedbacks.length})</Typography>
              {data.writtenFeedbacks.map((feedback) => (
                <Paper
                  key={feedback.id}
                  className="flex flex-col gap-2 border border-surface-200 p-4 dark:border-surface-700"
                >
                  <Typography variant="caption" color="secondary">
                    {formatFeedbackTimestamp(feedback.createdAt)}
                  </Typography>
                  <Typography variant="body2" className="whitespace-pre-wrap">
                    {feedback.text}
                  </Typography>
                </Paper>
              ))}
            </div>
          ) : (
            <Typography variant="body2" className="text-gray-600 dark:text-gray-300">
              Keine Freitext-Feedbacks vorhanden.
            </Typography>
          )}
        </div>
      ) : null}
    </AdminToolPage>
  );
};

export default AdminFeedbackResults;
